/**
 * BandIcon — renders a Band indicator icon on lesson cells.
 *
 * Outline = assigned but no entry yet (pending).
 * Solid/colored = completed.
 * Uses the Band's configured color.
 *
 * @module baende/components
 */

import { BookOpen } from "lucide-react";
import * as LucideIcons from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface BandIconProps {
  iconName?: string;
  color?: string;
  completed?: boolean;
  size?: number;
  className?: string;
  title?: string;
}

/** Resolve a lucide icon name string to a component */
function resolveIcon(iconName: string): LucideIcon {
  const icons = LucideIcons as Record<string, unknown>;
  const pascalName = iconName
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  return (icons[pascalName] as LucideIcon) ?? BookOpen;
}

export function BandIcon({
  iconName = "book-open",
  color = "#10b981",
  completed = false,
  size = 14,
  className = "",
  title,
}: BandIconProps) {
  const IconComponent = resolveIcon(iconName);

  const icon = (
    <IconComponent
      className={`shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        color: completed ? color : undefined,
        opacity: completed ? 1 : 0.5,
      }}
      fill={completed ? color : "none"}
      strokeWidth={completed ? 1.5 : 2}
    />
  );

  if (title) {
    return <span title={title}>{icon}</span>;
  }
  return icon;
}
